'use client';
import React, { useEffect, useState, useMemo } from 'react';
import {
  Grid, Paper, Typography, Box,
  TextField, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Avatar,
  Chip, IconButton, Pagination, InputAdornment, Dialog, DialogTitle, DialogContent, Button, Select, TableSortLabel,
  useTheme
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import Autocomplete from "@mui/material/Autocomplete";
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useUser } from "../context/UserContext";
import PageLoader from '../components/PageLoader';
import SectionLoader from '../components/SectionLoader';
import Toast from "../components/Toast";
import ConfirmDialog from "../components/ConfirmDialog";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CategoryIcon from '@mui/icons-material/Category';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Tooltip from '@mui/material/Tooltip';
import SettingsIcon from '@mui/icons-material/Settings';
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import DraftsIcon from "@mui/icons-material/Drafts";
import DraftManager from "../utils/draftManager";
import DraftsModal from "../components/DraftsModal";
import HelpTooltip from "../components/HelpTooltip";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArchiveManager from "../utils/archiveManager";

function StatCard({ label, count, IconComponent, active = false, onClick }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const bgDefault = isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)';
  const bgActive = isLight
    ? 'linear-gradient(135deg, rgba(13, 148, 136, 0.12) 0%, rgba(13, 148, 136, 0.03) 100%)'
    : 'linear-gradient(135deg, rgba(76, 161, 175, 0.28) 0%, rgba(44, 62, 80, 0.5) 100%)';
  const borderDefault = isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)';
  const borderActive = isLight ? '2px solid #0D9488' : '2px solid #4CA1AF';
  const countColor = isLight ? (active ? '#0D9488' : '#0F172A') : '#FFFFFF';
  const labelColor = isLight ? (active ? '#0F172A' : '#64748B') : (active ? '#FFFFFF' : 'rgba(255,255,255,0.7)');
  const iconBg = active ? (isLight ? '#0D9488' : '#4CA1AF') : (isLight ? 'rgba(13, 148, 136, 0.1)' : 'rgba(76, 161, 175, 0.2)');
  const iconColor = active ? '#FFFFFF' : (isLight ? '#0D9488' : '#4CA1AF');

  return (
    <Card
      onClick={onClick}
      variant="outlined"
      sx={{
        height: "100%",
        minWidth: 0,
        width: "100%",
        borderRadius: 2.5,
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        background: active ? bgActive : bgDefault,
        backdropFilter: 'blur(20px)',
        border: active ? borderActive : borderDefault,
        boxShadow: active
          ? (isLight ? '0 4px 16px rgba(13, 148, 136, 0.18)' : '0 8px 24px rgba(76, 161, 175, 0.35)')
          : (isLight ? '0 1px 3px rgba(0, 0, 0, 0.05)' : '0 2px 8px rgba(0, 0, 0, 0.1)'),
        position: 'relative',
        overflow: 'hidden',
        '&:hover': onClick ? {
          transform: 'translateY(-3px)',
          borderColor: active ? borderActive : (isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.5)'),
          background: active
            ? bgActive
            : (isLight ? '#FFFFFF' : 'rgba(76, 161, 175, 0.12)'),
          boxShadow: isLight ? '0 8px 20px rgba(15, 23, 42, 0.08)' : '0 8px 20px rgba(0, 0, 0, 0.3)',
        } : {},
      }}
    >
      <CardContent sx={{ minHeight: 80, p: '16px !important' }}>
        <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
          <Box
            sx={{
              height: 48,
              width: 48,
              background: iconBg,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              flexShrink: 0,
              transition: 'all 0.25s ease',
              boxShadow: active ? (isLight ? '0 4px 12px rgba(13, 148, 136, 0.25)' : '0 4px 12px rgba(76, 161, 175, 0.4)') : 'none',
            }}
          >
            <IconComponent sx={{ fontSize: 28, color: iconColor }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="h5" fontWeight="bold" noWrap sx={{ color: countColor }}>
                {count}
              </Typography>
              {active && (
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: 1,
                    bgcolor: isLight ? 'rgba(13, 148, 136, 0.12)' : 'rgba(76, 161, 175, 0.3)',
                    border: isLight ? '1px solid rgba(13, 148, 136, 0.35)' : '1px solid rgba(76, 161, 175, 0.6)',
                    color: isLight ? '#0D9488' : '#64d2e2',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Active
                </Box>
              )}
            </Box>
            <Typography variant="body2" noWrap sx={{ color: labelColor, fontWeight: active ? 600 : 400 }}>
              {label}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const SpecialPrayerCategoryList = ({ hideHeader = false, onNavigateTab }) => {
  const muiTheme = useTheme();
  const navigate = useNavigate();
  const isLight = muiTheme.palette.mode === 'light';
  const [stats, setStats] = useState([
    { title: 'Total', count: 0 },
    { title: 'Active', count: 0 },
    { title: 'Answered', count: 0 },
    { title: 'This Month', count: 0 },
  ]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState(null); // or orgOptions[0] for default
  const [editCategory, setEditCategory] = useState(null);
  const [selected, setSelected] = useState([]);
  const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];
  const { user, isSwitched, effectiveOrgId, orgViewMode } = useUser();
  const isSwitchedOrgMode = Boolean(
    isSwitched ||
    orgViewMode ||
    effectiveOrgId ||
    user?.is_switched_from_super_admin ||
    sessionStorage.getItem('gpn_sa_token') ||
    localStorage.getItem('gpn_sa_token') ||
    sessionStorage.getItem('gpn_org_view_mode') ||
    localStorage.getItem('gpn_org_view_mode')
  );
  const showOrganizationColumn = Boolean(user?.role === 1 && !isSwitchedOrgMode && !user?.org_id);
  const [addCategory, setAddCategory] = useState(false);
  const [selectedCategoryForView, setSelectedCategoryForView] = useState(null);
  const [categoryDetailModalOpen, setCategoryDetailModalOpen] = useState(false);

  const [category, setCategory] = useState('');
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsList, setDraftsList] = useState(() => DraftManager.getDrafts('special-prayer-categories'));
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [archivedList, setArchivedList] = useState(() => ArchiveManager.getArchivedItems('special-prayer-categories'));
  const [filterActiveTab, setFilterActiveTab] = useState('all');
  const [sortOrder, setSortOrder] = useState('asc');

  const API_URL = process.env.REACT_APP_API_URL || 'http://45.194.3.128:4000/api/admin/';
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // Auto-save draft on beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (addCategory && category && category.trim()) {
        DraftManager.saveDraft('special-prayer-categories', { name: category }, activeDraftId);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [addCategory, category, activeDraftId]);

  const handleManualSaveDraft = () => {
    if (!category || !category.trim()) {
      setToast({ open: true, message: "Please enter a category name before saving draft.", severity: "warning" });
      return;
    }
    const res = DraftManager.saveDraft('special-prayer-categories', { name: category }, activeDraftId);
    if (res.success) {
      setActiveDraftId(res.draftId);
      setDraftsList(res.drafts || []);
      setToast({ open: true, message: "Category draft saved successfully!", severity: "success" });
    } else {
      setToast({ open: true, message: res.message || "Failed to save draft", severity: "error" });
    }
  };

  const handleContinueDraft = (draft) => {
    if (!draft || !draft.data) return;
    setCategory(draft.data.name || '');
    setActiveDraftId(draft.id);
    setAddCategory(true);
    setToast({ open: true, message: `Loaded draft: ${draft.title || 'Untitled'}`, severity: "info" });
  };

  const handleDeleteDraft = (draftId) => {
    const res = DraftManager.deleteDraft('special-prayer-categories', draftId);
    setDraftsList(res.drafts || []);
    if (activeDraftId === draftId) setActiveDraftId(null);
    setToast({ open: true, message: "Draft removed.", severity: "info" });
  };

  const handleArchive = (cat) => {
    setConfirmDialog({
      open: true,
      title: "Archive Special Prayer Category",
      message: "Are you sure you want to archive this category? It will no longer be visible to mobile app users until restored.",
      onConfirm: () => {
        ArchiveManager.archiveItem('special-prayer-categories', cat);
        setArchivedList(ArchiveManager.getArchivedItems('special-prayer-categories'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        setToast({ open: true, message: "Category archived and hidden from mobile app.", severity: "info" });
      }
    });
  };

  const handleRestore = (catId) => {
    ArchiveManager.restoreItem('special-prayer-categories', catId);
    setArchivedList(ArchiveManager.getArchivedItems('special-prayer-categories'));
    setToast({ open: true, message: "Category restored to active status!", severity: "success" });
  };

  const handlePermanentDelete = (catId) => {
    setConfirmDialog({
      open: true,
      title: "Permanently Delete Category",
      message: "Are you sure you want to permanently delete this archived category? This cannot be undone.",
      onConfirm: () => {
        ArchiveManager.deletePermanently('special-prayer-categories', catId);
        setArchivedList(ArchiveManager.getArchivedItems('special-prayer-categories'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        setToast({ open: true, message: "Category permanently removed.", severity: "success" });
      }
    });
  };


  // Fetch category data (mock or API)
  useEffect(() => {
    fetchCategories();
  }, [search, status, page, organization, fromDate, toDate]);

  const fetchCategories = async () => {
    // Replace this with your actual API call
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const targetOrg = effectiveOrgId || (organization && organization.id !== 0 ? organization?.id : null);
      const response = await fetch(`${API_URL}special-prayer-categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page: page,
          search: search,
          status: status,
          organization: targetOrg || null,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        })
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

         }

      setCategories(data.data.categories);
      setOrganizations(data.data.organisations);
      setTotalPages(data.data.totalPages);

      const catList = Array.isArray(data?.data?.categories) ? data.data.categories : [];
      const totalCount = data?.totalCount ?? data?.data?.totalCategories ?? catList.length;
      const activeCount = data?.activePrayers ?? data?.data?.activeCategories ?? catList.filter(c => c.status === 'active').length;
      const inactiveCount = data?.answered ?? data?.data?.inactiveCategories ?? catList.filter(c => c.status === 'inactive').length;
      const thisMonthCount = data?.thisMonth ?? data?.data?.thisMonthCategories ?? catList.filter(c => {
        if (!c.created_at) return false;
        const d = new Date(c.created_at);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length;

      setStats([
        { title: 'Total', count: totalCount },
        { title: 'Active', count: activeCount },
        { title: 'Inactive', count: inactiveCount },
        { title: 'This Month', count: thisMonthCount },
      ]);

    } catch (err) {
      // setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditCategory(category);
    // Handle edit action
    console.log('Edit', category);
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Special Prayer Category',
      message: 'Are you sure you want to delete this category? This action cannot be undone.',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });

        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}delete-special-prayer-category`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: id,
            status: 3
          })
        });
        const data = await response.json();


        if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

          setToast({ open: true, message: data?.message || 'Failed to delete song', severity: "error" });
         }
          setToast({ open: true, message: "Category deleted successfully", severity: "success" });
          setTimeout(() => {
            window.location.reload();
          }, 1500);
       
      }
    });
  };


  const handleSaveEdit = async () => {
    const token = localStorage.getItem('token');

    if (!editCategory.name || !editCategory.name.trim()) {
      setToast({ open: true, message: 'Category name is required', severity: "error" });
      return false;
    }

    const response = await fetch(`${API_URL}update-special-prayer-category`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        // ❌ Do NOT set 'Content-Type' when sending FormData
      },
      body: JSON.stringify({
        id: editCategory.id,
        name: editCategory.name,
      })
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

          setToast({ open: true, message: data?.message || 'Failed to delete song', severity: "error" });
    }
      setToast({ open: true, message: "Category updated successfully", severity: "success" });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    
  };
  const handleSaveAdd = async () => {

   if (!category || !category.trim()) {
      setToast({ open: true, message: 'Category name is required', severity: "error" });
      return false;
    }
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}add-special-prayer-category`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: category,
      })
    });
    const data = await response.json();


    if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

          setToast({ open: true, message: data?.message || 'Failed to delete song', severity: "error" });

    }
      setToast({ open: true, message: "Category added successfully", severity: "success" });
      if (activeDraftId) {
        DraftManager.deleteDraft('special-prayer-categories', activeDraftId);
        setActiveDraftId(null);
        setDraftsList(DraftManager.getDrafts('special-prayer-categories'));
      }
      setTimeout(() => {
        window.location.reload();
      }, 1500);
   
  };




  const displayedCategories = useMemo(() => {
    const list = filterActiveTab === "archived"
      ? archivedList
      : categories.filter(c => !archivedList.some(a => String(a.id) === String(c.id)));
    return [...list].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      if (sortOrder === 'asc') return nameA.localeCompare(nameB);
      if (sortOrder === 'desc') return nameB.localeCompare(nameA);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [categories, archivedList, filterActiveTab, sortOrder]);

  return (
    <>
      {!hideHeader && (
        <Helmet>
          <title>Special Prayer Categories - GPN</title>
        </Helmet>
      )}

      <Box p={hideHeader ? 0 : 2}>
        {!hideHeader && (
          <Box mb={5}>
            <Typography variant="h5" fontWeight={700}>
              Special Prayer Category Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage prayer categories, including name, status, and organization.
            </Typography>
          </Box>
        )}

        {/* Compact 2-Row Unified Toolbar */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            background: isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.04)",
            border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.08)",
            display: "flex",
            flexDirection: "column",
            gap: 1.2,
            boxShadow: isLight ? "0 1px 3px rgba(15, 23, 42, 0.04)" : "none",
          }}
        >
          {/* Row 1: Title, Total, Search, Sort & Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.2,
              pb: 1,
              borderBottom: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255, 255, 255, 0.06)",
            }}
          >
            {/* Left: Heading & Total */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: isLight ? "#0F172A" : "#FFFFFF", fontSize: "1rem" }}>
                Prayer Categories
              </Typography>
              <Chip
                label={
                  filterActiveTab === "archived"
                    ? `${archivedList.length} Archived`
                    : status === "active"
                    ? `${categories.filter(c => c.status === "active" || !c.status).length} Active`
                    : status === "inactive"
                    ? `${categories.filter(c => c.status === "inactive").length} Inactive`
                    : `${categories.length} Total`
                }
                size="small"
                sx={{
                  bgcolor: isLight ? "#E0F2FE" : "rgba(76, 161, 175, 0.2)",
                  color: isLight ? "#0284C7" : "#64d2e2",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: 22,
                }}
              />
            </Box>

            {/* Right: Search, Sort, Drafts, Add Category */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search categories..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: "100%", sm: 200 }, "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" } }}
              />

              <Select
                size="small"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                displayEmpty
                renderValue={(val) => `Sort: ${val === "asc" ? "A → Z" : "Z → A"}`}
                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
              >
                <MenuItem value="asc">Name: A → Z (Alphabetical)</MenuItem>
                <MenuItem value="desc">Name: Z → A</MenuItem>
              </Select>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DraftsIcon sx={{ fontSize: 18 }} />}
                onClick={() => {
                  setDraftsList(DraftManager.getDrafts('special-prayer-categories'));
                  setDraftsOpen(true);
                }}
                sx={{
                  py: 0.5,
                  px: 1.2,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  borderColor: isLight ? "#CBD5E1" : "rgba(255, 183, 77, 0.5)",
                  color: isLight ? "#B45309" : "#ffb74d",
                  "&:hover": { borderColor: "#D97706" },
                }}
              >
                Drafts ({draftsList.length}/3)
              </Button>

              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                onClick={() => setAddCategory(true)}
                sx={{
                  background: isLight ? "#0D9488" : "#177373",
                  py: 0.6,
                  px: 1.5,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1.5,
                  "&:hover": { background: isLight ? "#0F766E" : "#135d5d" },
                }}
              >
                Add Category
              </Button>
            </Box>
          </Box>

          {/* Row 2: Status Chips + Date / Org Filters */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Status Quick Chips */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
              <Chip
                size="small"
                clickable
                label="← Back to Prayer Closet"
                onClick={() => {
                  if (onNavigateTab) {
                    onNavigateTab('prayers');
                  } else {
                    navigate('/admin/prayer-closet?tab=prayers');
                  }
                }}
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  bgcolor: isLight ? "#F1F5F9" : "rgba(255, 255, 255, 0.08)",
                  color: isLight ? "#0D9488" : "#64d2e2",
                  border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(76, 161, 175, 0.4)",
                  height: 26,
                  "&:hover": { bgcolor: isLight ? "#E2E8F0" : "rgba(255, 255, 255, 0.15)" },
                }}
              />

              <Chip
                size="small"
                label={`Categories (${categories.length})`}
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  bgcolor: isLight ? "#0D9488" : "#177373",
                  color: "#FFFFFF",
                  height: 26,
                }}
              />

              <Box sx={{ width: "1px", height: "18px", bgcolor: isLight ? "#CBD5E1" : "rgba(255, 255, 255, 0.2)", mx: 0.3 }} />

              {[
                { label: "All", value: "", count: categories.length },
                { label: "Active", value: "active", count: categories.filter(c => c.status === "active" || !c.status).length },
                { label: "Inactive", value: "inactive", count: categories.filter(c => c.status === "inactive").length },
              ].map((item) => {
                const isActive = filterActiveTab !== "archived" && status === item.value;
                return (
                  <Chip
                    key={item.label}
                    size="small"
                    clickable
                    label={`${item.label} (${item.count})`}
                    onClick={() => {
                      setFilterActiveTab('all');
                      setStatus(status === item.value ? '' : item.value);
                      setPage(1);
                    }}
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: isActive ? 700 : 500,
                      bgcolor: isActive ? "#0F766E" : (isLight ? "#F1F5F9" : "rgba(255,255,255,0.08)"),
                      color: isActive ? "#FFFFFF" : (isLight ? "#475569" : "rgba(255,255,255,0.8)"),
                      height: 26,
                      "&:hover": { bgcolor: isActive ? "#115E59" : (isLight ? "#E2E8F0" : "rgba(255,255,255,0.15)") },
                    }}
                  />
                );
              })}

              <Chip
                size="small"
                clickable
                label={`Archived (${archivedList.length})`}
                onClick={() => {
                  setFilterActiveTab(filterActiveTab === "archived" ? "all" : "archived");
                  setPage(1);
                }}
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: filterActiveTab === "archived" ? 700 : 500,
                  bgcolor: filterActiveTab === "archived" ? "#D97706" : (isLight ? "#FEF3C7" : "rgba(217, 119, 6, 0.2)"),
                  color: filterActiveTab === "archived" ? "#FFFFFF" : (isLight ? "#92400E" : "#FBBF24"),
                  height: 26,
                  "&:hover": { bgcolor: filterActiveTab === "archived" ? "#B45309" : (isLight ? "#FDE68A" : "rgba(217, 119, 6, 0.3)") },
                }}
              />
            </Box>

            {/* Filter Dropdowns: Dates, Org */}
            <Box sx={{ display: "flex", gap: 0.8, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="From"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                inputProps={{ max: "9999-12-31" }}
                sx={{ width: 130, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }}
              />

              <TextField
                size="small"
                label="To"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                inputProps={{ max: "9999-12-31" }}
                sx={{ width: 130, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }}
              />

              {user?.role === 1 && !isSwitched && (
                <Autocomplete
                  size="small"
                  value={organization}
                  onChange={(event, newValue) => {
                    setOrganization(newValue);
                    setPage(1);
                  }}
                  options={orgOptions}
                  getOptionLabel={(option) => option?.org_name || ""}
                  renderInput={(params) => <TextField {...params} label="Org" sx={{ "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }} />}
                  sx={{ width: 170 }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
            </Box>
          </Box>
        </Paper>

        {/* Category Table */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.08)",
            bgcolor: isLight ? "#FFFFFF" : undefined,
            boxShadow: isLight ? "0 1px 3px rgba(15,23,42,0.05)" : undefined,
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: isLight ? "#F8FAFC" : "rgba(255,255,255,0.02)" }}>
                <TableCell sx={{ fontWeight: 700, color: isLight ? "#475569" : "#FFFFFF", borderBottom: isLight ? "1px solid #E2E8F0" : undefined, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700, color: isLight ? "#475569" : "#FFFFFF", borderBottom: isLight ? "1px solid #E2E8F0" : undefined, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Active Prayers</TableCell>
                {showOrganizationColumn && <TableCell sx={{ fontWeight: 700, color: isLight ? "#475569" : "#FFFFFF", borderBottom: isLight ? "1px solid #E2E8F0" : undefined, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Organization</TableCell>}
                <TableCell sx={{ fontWeight: 700, color: isLight ? "#475569" : "#FFFFFF", borderBottom: isLight ? "1px solid #E2E8F0" : undefined, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: isLight ? "#475569" : "#FFFFFF", borderBottom: isLight ? "1px solid #E2E8F0" : undefined, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700, color: isLight ? "#475569" : "#FFFFFF", borderBottom: isLight ? "1px solid #E2E8F0" : undefined, fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <SectionLoader />
              ) : null}

              {!loading && displayedCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={user?.role === 1 ? 6 : 5} align="center" sx={{ py: 4, color: isLight ? '#64748B' : 'text.secondary', borderBottom: 'none' }}>
                    {filterActiveTab === "archived" ? "No archived special prayer categories found" : "No categories found."}
                  </TableCell>
                </TableRow>
              ) : null}

              {!loading && displayedCategories.map((cat) => (
                <TableRow
                  key={cat.id}
                  hover
                  onClick={() => {
                    setSelectedCategoryForView(cat);
                    setCategoryDetailModalOpen(true);
                  }}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: isLight ? '#F0FDFA !important' : 'rgba(76, 161, 175, 0.12) !important' },
                    '& td': { borderBottom: isLight ? '1px solid #F1F5F9' : undefined }
                  }}
                >
                  {/* Category Name with Avatar */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.2}>
                      <Avatar
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '10px',
                          bgcolor: isLight ? "#F0FDFA" : "#f3f4f6",
                          color: isLight ? "#0D9488" : "#2C3E50",
                          border: isLight ? "1px solid #CCFBF1" : "none",
                          fontWeight: 700,
                          fontSize: '1rem'
                        }}
                      >
                        {cat.name?.[0]?.toUpperCase() || "?"}
                      </Avatar>
                      <Typography fontWeight={600} sx={{ color: isLight ? '#0F172A' : '#FFFFFF', fontSize: '0.92rem' }}>
                        {cat.name}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Active Prayers */}
                  <TableCell sx={{ color: isLight ? '#334155' : undefined, fontWeight: 600 }}>
                    <Tooltip title={`View prayers in ${cat.name}`}>
                      <Chip
                        size="small"
                        clickable
                        label={`${cat._count?.session_prayers || 0} prayers`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onNavigateTab) {
                            onNavigateTab('prayers');
                          }
                          navigate(`/admin/prayer-closet?tab=prayers&category=${cat.id}`);
                        }}
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.15)",
                          color: isLight ? "#0D9488" : "#64d2e2",
                          "&:hover": { bgcolor: isLight ? "rgba(13, 148, 136, 0.2)" : "rgba(76, 161, 175, 0.3)" }
                        }}
                      />
                    </Tooltip>
                  </TableCell>

                  {/* Organization (Admin Only) */}
                  {showOrganizationColumn && (
                    <TableCell sx={{ color: isLight ? '#64748B' : undefined }}>
                      {cat.origanisation?.org_name || "Default Category"}
                    </TableCell>
                  )}

                  {/* Status Chip */}
                  <TableCell>
                    <Chip
                      label={cat.deleted_at ? "Deleted" : cat.status || "active"}
                      color={
                        cat.deleted_at
                          ? "error"
                          : cat.status === "active"
                            ? "success"
                            : cat.status === "inactive"
                              ? "error"
                              : "default"
                      }
                      size="small"
                      sx={{ fontWeight: 600, borderRadius: '8px' }}
                    />
                  </TableCell>

                  {/* Created Date */}
                  <TableCell sx={{ color: isLight ? '#64748B' : undefined }}>
                    {cat.created_at ? new Date(cat.created_at).toLocaleDateString() : 'N/A'}
                  </TableCell>

                  {/* Actions */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {filterActiveTab === "archived" ? (
                      <Box display="flex" gap={1}>
                        <Tooltip title="Restore to active (Make visible in mobile app)">
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); handleRestore(cat.id); }}
                            sx={{
                              color: isLight ? '#059669' : '#4CAF50',
                              bgcolor: isLight ? '#ECFDF5' : 'transparent',
                              '&:hover': { background: isLight ? '#D1FAE5' : 'rgba(76, 175, 80, 0.1)' }
                            }}
                          >
                            <UnarchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Permanently delete">
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); handlePermanentDelete(cat.id); }}
                            sx={{
                              color: isLight ? '#DC2626' : '#EF4444',
                              bgcolor: isLight ? '#FEF2F2' : 'transparent',
                              '&:hover': { background: isLight ? '#FEE2E2' : 'rgba(239, 68, 68, 0.1)' }
                            }}
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : cat.deleted_at != null ? (
                      <Typography variant="body2" color="textSecondary">Deleted</Typography>
                    ) : (
                      <>
                        {((Number(cat.org_id) === Number(user.org_id)) || user.role === 1) && (
                          <Box display="flex" gap={0.8}>
                            <Tooltip title="Edit Category">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleEdit(cat); }}
                                sx={{
                                  color: isLight ? '#0D9488' : '#4CA1AF',
                                  bgcolor: isLight ? '#F0FDFA' : 'transparent',
                                  border: isLight ? '1px solid #CCFBF1' : 'none',
                                  '&:hover': { background: isLight ? '#CCFBF1' : 'rgba(76, 161, 175, 0.1)' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Archive (Hide from mobile app)">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleArchive(cat); }}
                                sx={{
                                  color: isLight ? '#D97706' : '#ffb74d',
                                  bgcolor: isLight ? '#FFFBEB' : 'transparent',
                                  border: isLight ? '1px solid #FDE68A' : 'none',
                                  '&:hover': { background: isLight ? '#FEF3C7' : 'rgba(255, 183, 77, 0.1)' }
                                }}
                              >
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Category">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleDelete(cat.id); }}
                                sx={{
                                  color: isLight ? '#DC2626' : '#EF4444',
                                  bgcolor: isLight ? '#FEF2F2' : 'transparent',
                                  border: isLight ? '1px solid #FECACA' : 'none',
                                  '&:hover': { background: isLight ? '#FEE2E2' : 'rgba(239, 68, 68, 0.1)' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!loading && categories.length > 0 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
            />
          </Box>
        )}

        {editCategory && (
          <Dialog
            open={!!editCategory}
            onClose={() => setEditCategory(null)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                background: isLight ? '#FFFFFF' : undefined,
                borderRadius: '16px',
                border: isLight ? '1px solid #E2E8F0' : undefined,
                color: isLight ? '#0F172A' : undefined,
                boxShadow: isLight ? '0 20px 40px rgba(15,23,42,0.12)' : undefined,
              }
            }}
          >
            <DialogTitle sx={{ fontWeight: 700, color: isLight ? '#0F172A' : undefined, borderBottom: isLight ? '1px solid #E2E8F0' : undefined }}>Edit Category</DialogTitle>
            <DialogContent sx={{ pt: '20px !important' }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                <TextField
                  label="Category Name"
                  value={editCategory.name}
                  onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })}
                  InputLabelProps={{ sx: { color: isLight ? '#64748B' : undefined } }}
                  sx={{
                    ...(isLight && {
                      '& .MuiOutlinedInput-root': {
                        color: '#0F172A',
                        borderRadius: '10px',
                        bgcolor: '#F8FAFC',
                        '& fieldset': { borderColor: '#CBD5E1' },
                        '&:hover fieldset': { borderColor: '#0D9488' },
                        '&.Mui-focused fieldset': { borderColor: '#0D9488' },
                      }
                    })
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
                <Button onClick={() => setEditCategory(null)} sx={{ color: isLight ? '#64748B' : undefined, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleSaveEdit}
                  sx={{
                    background: isLight ? '#0D9488' : undefined,
                    color: '#FFFFFF',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    '&:hover': { background: isLight ? '#0F766E' : undefined }
                  }}
                >
                  Save
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        )}
        {addCategory && (
          <Dialog
            open={!!addCategory}
            onClose={() => setAddCategory(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                background: isLight ? '#FFFFFF' : undefined,
                borderRadius: '16px',
                border: isLight ? '1px solid #E2E8F0' : undefined,
                color: isLight ? '#0F172A' : undefined,
                boxShadow: isLight ? '0 20px 40px rgba(15,23,42,0.12)' : undefined,
              }
            }}
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: isLight ? '1px solid #E2E8F0' : undefined }}>
              <span style={{ fontWeight: 700, color: isLight ? '#0F172A' : undefined }}>Add Category</span>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DraftsIcon />}
                  onClick={() => {
                    setDraftsList(DraftManager.getDrafts('special-prayer-categories'));
                    setDraftsOpen(true);
                  }}
                  sx={{
                    borderColor: isLight ? '#F59E0B' : 'rgba(255, 183, 77, 0.5)',
                    color: isLight ? '#B45309' : '#ffb74d',
                    bgcolor: isLight ? '#FFFBEB' : 'transparent',
                    fontSize: '0.75rem',
                  }}
                >
                  Drafts ({draftsList.length}/3)
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleManualSaveDraft}
                  sx={{
                    borderColor: isLight ? '#0D9488' : 'rgba(76, 161, 175, 0.5)',
                    color: isLight ? '#0F766E' : '#64d2e2',
                    bgcolor: isLight ? '#F0FDFA' : 'transparent',
                    fontSize: '0.75rem',
                  }}
                >
                  Save Draft
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: '20px !important' }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    label="Category Name"
                    value={category || ''}
                    onChange={(e) => setCategory(e.target.value)}
                    InputLabelProps={{ sx: { color: isLight ? '#64748B' : undefined } }}
                    sx={{
                      ...(isLight && {
                        '& .MuiOutlinedInput-root': {
                          color: '#0F172A',
                          borderRadius: '10px',
                          bgcolor: '#F8FAFC',
                          '& fieldset': { borderColor: '#CBD5E1' },
                          '&:hover fieldset': { borderColor: '#0D9488' },
                          '&.Mui-focused fieldset': { borderColor: '#0D9488' },
                        }
                      })
                    }}
                  />
                  <HelpTooltip
                    title="Special Prayer Category"
                    purpose="Classification for specialized devotional or strategic prayer campaigns"
                    howItWorks="Linked to Special Prayers to group them thematically in the mobile app"
                    mobileEffect="Users explore special prayer categories and campaigns on the mobile app home"
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
                <Button onClick={() => setAddCategory(false)} sx={{ color: isLight ? '#64748B' : undefined, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleSaveAdd}
                  sx={{
                    background: isLight ? '#0D9488' : '#177373',
                    color: '#FFFFFF',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    '&:hover': { background: isLight ? '#0F766E' : '#135d5d' }
                  }}
                >
                  Save
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        )}

        {/* Category Detail Modal */}
        {categoryDetailModalOpen && selectedCategoryForView && (
          <Dialog
            open={categoryDetailModalOpen}
            onClose={() => {
              setCategoryDetailModalOpen(false);
              setSelectedCategoryForView(null);
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "16px",
                bgcolor: isLight ? "#FFFFFF" : "#1e293b",
                color: isLight ? "#0F172A" : "#FFFFFF",
                boxShadow: isLight ? "0 20px 40px rgba(0,0,0,0.15)" : "0 20px 40px rgba(0,0,0,0.5)",
              }
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.1)",
                pb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CategoryIcon sx={{ color: isLight ? "#0D9488" : "#4CA1AF" }} />
                <Typography variant="h6" fontWeight={700}>
                  Special Prayer Category Details
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setCategoryDetailModalOpen(false);
                  setSelectedCategoryForView(null);
                }}
                sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.7)" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "14px",
                      bgcolor: isLight ? "#F0FDFA" : "rgba(13, 148, 136, 0.2)",
                      color: isLight ? "#0D9488" : "#64d2e2",
                      border: isLight ? "2px solid #CCFBF1" : "2px solid rgba(76, 161, 175, 0.3)",
                      fontWeight: 700,
                      fontSize: "1.5rem",
                    }}
                  >
                    {selectedCategoryForView.name?.[0]?.toUpperCase() || "?"}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                      {selectedCategoryForView.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                      <Chip
                        label={selectedCategoryForView.deleted_at ? "Deleted" : selectedCategoryForView.status || "active"}
                        color={
                          selectedCategoryForView.deleted_at
                            ? "error"
                            : selectedCategoryForView.status === "active"
                              ? "success"
                              : "default"
                        }
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                      <Chip
                        label={`${selectedCategoryForView._count?.session_prayers || 0} prayers`}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setCategoryDetailModalOpen(false);
                          if (onNavigateTab) onNavigateTab("prayers");
                          navigate(`/admin/prayer-closet?tab=prayers&category=${selectedCategoryForView.id}`);
                        }}
                        sx={{
                          cursor: "pointer",
                          fontWeight: 600,
                          color: isLight ? "#0D9488" : "#64d2e2",
                          borderColor: isLight ? "#0D9488" : "rgba(76, 161, 175, 0.4)",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255,255,255,0.03)",
                    borderColor: isLight ? "#E2E8F0" : "rgba(255,255,255,0.08)",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                        Organization
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedCategoryForView.origanisation?.org_name || "Default Category"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                        Created Date
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedCategoryForView.created_at ? new Date(selectedCategoryForView.created_at).toLocaleDateString() : "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
                <Button
                  onClick={() => {
                    setCategoryDetailModalOpen(false);
                    setSelectedCategoryForView(null);
                  }}
                  sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.7)", textTransform: "none" }}
                >
                  Close
                </Button>
                {((Number(selectedCategoryForView.org_id) === Number(user.org_id)) || user.role === 1) && !selectedCategoryForView.deleted_at && (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      const catToEdit = selectedCategoryForView;
                      setCategoryDetailModalOpen(false);
                      setSelectedCategoryForView(null);
                      handleEdit(catToEdit);
                    }}
                    sx={{
                      background: isLight ? "#0D9488" : "#177373",
                      color: "#FFFFFF",
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { background: isLight ? "#0F766E" : "#135d5d" }
                    }}
                  >
                    Edit Category
                  </Button>
                )}
              </Box>
            </DialogContent>
          </Dialog>
        )}

      </Box>
      <Toast toast={toast} setToast={setToast} />
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        severity="error"
      />
      {/* Floating Settings Icon */}
      {!hideHeader && (
        <Tooltip title="Special Prayer Categories Settings" placement="left">
          <IconButton
            onClick={() => setSettingsOpen(true)}
            sx={{
              position: 'fixed', bottom: 32, right: 32,
              bgcolor: isLight ? '#FFFFFF' : 'rgba(76, 161, 175, 0.15)',
              border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(76, 161, 175, 0.4)',
              boxShadow: isLight ? '0 4px 14px rgba(15, 23, 42, 0.12)' : undefined,
              color: isLight ? '#0D9488' : '#64d2e2', width: 48, height: 48, zIndex: 1300, backdropFilter: 'blur(8px)',
              '&:hover': { bgcolor: isLight ? '#F8FAFC' : 'rgba(76, 161, 175, 0.3)', transform: 'rotate(20deg)' },
              transition: 'all 0.25s ease',
            }}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      )}
      <ModuleSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} moduleKey="special-prayer-categories" moduleTitle="Special Prayer Categories" />
      <DraftsModal
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        moduleKey="special-prayer-categories"
        moduleTitle="Special Prayer Categories"
        onContinueDraft={handleContinueDraft}
      />
    </>
  );
};

export default SpecialPrayerCategoryList;
